'use client';

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { cn } from '@/lib/cn';

/* ================================================================
   Delos Select — 极简白灰下拉选择器
   ================================================================ */

export interface SelectOption {
  value: string;
  label: React.ReactNode;
  group?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface SelectProps {
  id?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  ariaLabelledBy?: string;
  title?: string;
  variant?: 'default' | 'brand';
  /** Keep the menu below the trigger when a fixed direction is required. */
  placement?: 'auto' | 'bottom';
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
}

export function Select({
  id,
  ariaLabel,
  ariaDescribedBy,
  ariaLabelledBy,
  title,
  variant = 'default',
  placement = 'auto',
  value,
  onChange,
  options,
  placeholder,
  label,
  disabled = false,
  className,
  triggerClassName,
}: SelectProps) {
  const t = useTranslations('common');
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);
  const menuReady = Boolean(menuStyle);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const triggerId = id ?? `${listboxId}-trigger`;
  const labelId = `${listboxId}-label`;
  const entryDirection = useRef<'first' | 'last'>('first');
  const search = useRef({ text: '', time: 0 });
  const selected = options.find((option) => option.value === value);
  const groups = options.reduce<Array<{ label?: string; options: SelectOption[] }>>((result, option) => {
    const previous = result.at(-1);
    if (previous && previous.label === option.group) previous.options.push(option);
    else result.push({ label: option.group, options: [option] });
    return result;
  }, []);
  const groupCount = groups.filter(group => group.label).length;

  const updateMenuPosition = useCallback(() => {
    if (typeof window === 'undefined' || !triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportInset = 12;
    const gap = 6;
    const availableBelow = window.innerHeight - rect.bottom - viewportInset;
    const availableAbove = rect.top - viewportInset;
    const maxMenuHeight = variant === 'brand' ? 240 : 360;
    const rowHeight = window.matchMedia?.('(pointer: coarse)').matches ? 48 : variant === 'brand' ? 44 : 36;
    const estimatedHeight = Math.min(maxMenuHeight, options.length * rowHeight + groupCount * 28 + (variant === 'brand' ? 12 : 8));
    const openAbove = placement === 'auto' && availableBelow < estimatedHeight + gap && availableAbove > availableBelow;
    const availableHeight = openAbove ? availableAbove - gap : availableBelow - gap;
    // Compact toolbar triggers must not squeeze their menu labels into multiple lines.
    const width = Math.max(0, Math.min(Math.max(rect.width, 192), window.innerWidth - viewportInset * 2));
    const left = Math.min(
      Math.max(viewportInset, rect.left),
      Math.max(viewportInset, window.innerWidth - width - viewportInset),
    );

    setMenuStyle({
      margin: 0,
      top: openAbove ? 'auto' : Math.max(viewportInset, rect.bottom + gap),
      right: 'auto',
      bottom: openAbove ? Math.max(viewportInset, window.innerHeight - rect.top + gap) : 'auto',
      left,
      width,
      maxHeight: Math.max(0, Math.min(maxMenuHeight, availableHeight)),
      zIndex: 10050,
    });
  }, [options.length, groupCount, variant, placement]);

  useEffect(() => {
    if (!isOpen) {
      setMenuStyle(null);
      return;
    }

    updateMenuPosition();
    const handlePointerDown = (event: PointerEvent | FocusEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('focusin', handlePointerDown);
    // Consume Escape before an enclosing modal's cancellation handler.
    document.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('focusin', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!isOpen || !menuReady) return;
    const menu = menuRef.current;
    // A popover occupies the top layer and escapes dialog overflow clipping.
    menu?.showPopover?.();
    const enabled = Array.from(menu?.querySelectorAll<HTMLButtonElement>('[role="option"]:not(:disabled)') ?? []);
    const selectedOption = enabled.find(option => option.getAttribute('aria-selected') === 'true');
    const initial = selectedOption ?? (entryDirection.current === 'last' ? enabled.at(-1) : enabled[0]);
    initial?.focus({ preventScroll: true });
    initial?.scrollIntoView({ block: 'nearest' });
    return () => { if (menu?.hidePopover && menu.matches(':popover-open')) menu.hidePopover(); };
    // Repositioning must not reset the user's keyboard focus.
  }, [isOpen, menuReady]);

  const handleSelect = useCallback((option: SelectOption) => {
    if (option.disabled) return;
    onChange(option.value);
    setIsOpen(false);
    triggerRef.current?.focus();
  }, [onChange]);

  const handleOptionKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Tab') {
      triggerRef.current?.focus();
      setIsOpen(false);
      return;
    }
    const enabledOptions = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]:not(:disabled)') ?? [],
    );
    if (!enabledOptions.length) return;
    const currentIndex = enabledOptions.indexOf(event.currentTarget);
    if (event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      const now = Date.now();
      const previous = now - search.current.time < 700 ? search.current.text : '';
      const text = previous.length === 1 && previous === event.key.toLocaleLowerCase() ? previous : previous + event.key.toLocaleLowerCase();
      search.current = { text, time: now };
      const ordered = [...enabledOptions.slice(currentIndex + 1), ...enabledOptions.slice(0, currentIndex + 1)];
      const match = ordered.find(option => option.textContent?.trim().toLocaleLowerCase().startsWith(text));
      match?.focus();
      match?.scrollIntoView({ block: 'nearest' });
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? enabledOptions.length - 1
        : event.key === 'ArrowDown'
          ? (currentIndex + 1) % enabledOptions.length
          : (currentIndex - 1 + enabledOptions.length) % enabledOptions.length;
    enabledOptions[nextIndex]?.focus();
    enabledOptions[nextIndex]?.scrollIntoView({ block: 'nearest' });
  }, []);

  const dropdown = isOpen && menuStyle && typeof document !== 'undefined'
    ? createPortal(
        <div
          ref={menuRef}
          id={listboxId}
          role="listbox"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabel ? undefined : ariaLabelledBy ?? (label ? labelId : triggerId)}
          popover={typeof HTMLElement !== 'undefined' && 'showPopover' in HTMLElement.prototype ? 'manual' : undefined}
          data-system-select-menu
          onKeyDown={event => event.stopPropagation()}
          className={cn(
            'fixed overflow-y-auto rounded-2xl border border-hr-border bg-white/95 p-1.5 shadow-[var(--studio-shadow-overlay)] backdrop-blur-xl',
            'delos-scrollbar animate-in fade-in zoom-in-95 duration-150',
            variant === 'brand' && 'border-[#DCE5EB] p-1.5 shadow-[0_12px_36px_rgba(27,36,43,0.14)]',
          )}
          style={menuStyle}
        >
          {groups.map((group, index) => <div key={index} role={group.label ? 'group' : undefined} aria-label={group.label}>
            {group.label ? <div role="presentation" className="px-3 pb-1 pt-2 text-xs font-medium text-hr-text-secondary">{group.label}</div> : null}
            {group.options.map(option => (
            <button
              key={option.value}
              type="button"
              role="option"
              title={typeof option.label === 'string' ? option.label : undefined}
              aria-selected={option.value === value}
              disabled={option.disabled}
              onClick={() => handleSelect(option)}
              onKeyDown={handleOptionKeyDown}
              className={cn(
                'flex min-h-9 w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] leading-5 transition-colors [@media(pointer:coarse)]:min-h-12 focus-visible:outline-none focus-visible:ring-inset',
                variant === 'default' && 'focus-visible:bg-surface-overlay focus-visible:ring-1 focus-visible:ring-hr-border',
                option.value === value
                  ? 'bg-surface-overlay font-medium text-hr-text-primary'
                  : 'text-hr-text-primary hover:bg-surface-overlay',
                option.disabled && 'cursor-not-allowed opacity-40',
                variant === 'brand' && 'min-h-11 gap-3 px-3 text-sm focus-visible:ring-2 focus-visible:ring-[#4ABDE9]',
                variant === 'brand' && (option.value === value ? 'bg-[#E4F5FC] text-[#176B89]' : 'text-[#53636D] hover:bg-[#F2F6F8]'),
              )}
            >
              <span className="flex min-w-0 items-center gap-2">
                {option.icon}
                <span className="truncate whitespace-nowrap">{option.label}</span>
              </span>
              {option.value === value ? <Check aria-hidden className={cn('h-3.5 w-3.5 shrink-0', variant === 'brand' ? 'text-accent-hover' : 'text-hr-text-secondary')} /> : null}
            </button>
          ))}</div>)}
        </div>,
        // Keep options in the enclosing dialog's accessible and focus-trap subtree.
        triggerRef.current?.closest('dialog[open], [role="dialog"]') ?? document.body,
      )
    : null;

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      {label ? <label id={labelId} htmlFor={triggerId} className="cap-label mb-1.5 block">{label}</label> : null}
      <button
        data-studio-control
        ref={triggerRef}
        id={triggerId}
        title={title}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy ?? (label ? labelId : undefined)}
        aria-describedby={ariaDescribedBy}
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        onClick={() => {
          if (!disabled) setIsOpen((current) => !current);
        }}
        onKeyDown={(event) => {
          if (!disabled && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            entryDirection.current = event.key === 'ArrowUp' || event.key === 'End' ? 'last' : 'first';
            setIsOpen(true);
          }
        }}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-3 rounded-xl border border-hr-border bg-white px-3 [@media(pointer:coarse)]:min-h-11',
          'text-sm text-hr-text-primary transition-all duration-200',
          variant !== 'brand' && 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hr-border focus-visible:ring-offset-2',
          variant !== 'brand' && (isOpen ? 'border-hr-border bg-surface-raised' : 'hover:border-hr-border'),
          disabled && 'cursor-not-allowed opacity-50',
          variant === 'brand' && 'h-11 border-[#DCE5EB] bg-white font-medium text-[#176B89] shadow-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FA5B2] focus-visible:ring-offset-2',
          triggerClassName,
        )}
      >
        <span className={cn('min-w-0 truncate', !selected && 'text-hr-text-tertiary')}>
          {selected ? (
            <span className="flex min-w-0 items-center gap-2">
              {selected.icon}
              <span className="truncate">{selected.label}</span>
            </span>
          ) : placeholder ?? t('selectPlaceholder')}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-hr-text-tertiary transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
        />
      </button>
      {dropdown}
    </div>
  );
}
